<template>
  <div class="workspace-shell">
    <aside class="side-nav">
      <div class="brand-block">
        <p>Enterprise Console</p>
        <h2>用品协同中心</h2>
        <span>{{ session.user.company_name }}</span>
      </div>

      <el-menu :default-active="activeMenu" class="nav-menu" @select="handleMenuSelect">
        <el-menu-item index="dashboard">
          <el-icon><DataBoard /></el-icon>
          <span>企业看板</span>
        </el-menu-item>
        <el-menu-item index="supplies">
          <el-icon><OfficeBuilding /></el-icon>
          <span>用品管理</span>
        </el-menu-item>
        <el-menu-item index="requests">
          <el-icon><DocumentChecked /></el-icon>
          <span>申领审批</span>
        </el-menu-item>
        <el-menu-item index="replenishment">
          <el-icon><Goods /></el-icon>
          <span>补货管理</span>
        </el-menu-item>
        <el-menu-item index="employees">
          <el-icon><User /></el-icon>
          <span>员工账号</span>
        </el-menu-item>
        <el-menu-item index="notifications">
          <el-icon><Bell /></el-icon>
          <span>站内信</span>
        </el-menu-item>
        <el-menu-item index="profile">
          <el-icon><Setting /></el-icon>
          <span>个人信息</span>
        </el-menu-item>
      </el-menu>
    </aside>

    <main class="workspace-main">
      <header class="top-bar">
        <div>
          <p class="page-kicker">企业管理员工作台</p>
          <h1>{{ titleMap[activeMenu] }}</h1>
        </div>
        <WorkspaceActionBar
          class="top-actions"
          :unread-count="unreadCount"
          :username="session.user.username"
          role-label="企业管理员"
          @notifications="jumpNotifications"
          @profile="activeMenu = 'profile'"
          @logout="$emit('logout')"
        />
      </header>

      <AdminDashboardPanel v-if="activeMenu === 'dashboard'" />
      <AdminSuppliesPanel v-else-if="activeMenu === 'supplies'" />
      <AdminRequestsPanel
        v-else-if="activeMenu === 'requests'"
        @notifications-changed="loadUnreadCount"
      />
      <AdminReplenishmentPanel
        v-else-if="activeMenu === 'replenishment'"
        @notifications-changed="loadUnreadCount"
      />
      <AdminEmployeesPanel v-else-if="activeMenu === 'employees'" />
      <NotificationsPanel
        v-else-if="activeMenu === 'notifications'"
        @unread-change="handleUnreadChange"
      />
      <ProfilePanel
        v-else-if="activeMenu === 'profile'"
        :session="session"
        role-label="企业管理员"
        @session-updated="$emit('session-updated', $event)"
      />
    </main>
  </div>
</template>

<script>
import {
  Bell,
  DataBoard,
  DocumentChecked,
  Goods,
  OfficeBuilding,
  Setting,
  User
} from '@element-plus/icons-vue'
import { fetchUserNotificationUnreadCount } from '../services/notificationService'
import AdminDashboardPanel from './admin/AdminDashboardPanel.vue'
import AdminEmployeesPanel from './admin/AdminEmployeesPanel.vue'
import AdminReplenishmentPanel from './admin/AdminReplenishmentPanel.vue'
import AdminRequestsPanel from './admin/AdminRequestsPanel.vue'
import AdminSuppliesPanel from './admin/AdminSuppliesPanel.vue'
import NotificationsPanel from './shared/NotificationsPanel.vue'
import ProfilePanel from './shared/ProfilePanel.vue'
import WorkspaceActionBar from './WorkspaceActionBar.vue'

export default {
  name: 'AdminWorkspace',
  components: {
    AdminDashboardPanel,
    AdminEmployeesPanel,
    AdminReplenishmentPanel,
    AdminRequestsPanel,
    AdminSuppliesPanel,
    Bell,
    DataBoard,
    DocumentChecked,
    Goods,
    NotificationsPanel,
    OfficeBuilding,
    ProfilePanel,
    Setting,
    User,
    WorkspaceActionBar
  },
  props: {
    session: {
      type: Object,
      required: true
    }
  },
  emits: ['logout', 'session-updated'],
  data() {
    return {
      activeMenu: 'dashboard',
      unreadCountTimer: null,
      unreadCount: 0,
      titleMap: {
        dashboard: '企业看板',
        supplies: '用品管理',
        requests: '申领审批',
        replenishment: '补货管理',
        employees: '员工账号',
        notifications: '站内信',
        profile: '个人信息'
      }
    }
  },
  mounted() {
    this.startUnreadCountPolling()
  },
  beforeUnmount() {
    this.stopUnreadCountPolling()
  },
  methods: {
    handleMenuSelect(index) {
      this.activeMenu = index
    },
    jumpNotifications() {
      this.activeMenu = 'notifications'
      this.loadUnreadCount()
    },
    handleUnreadChange(count) {
      this.unreadCount = count
    },
    startUnreadCountPolling() {
      this.loadUnreadCount()
      this.stopUnreadCountPolling()
      this.unreadCountTimer = window.setInterval(() => {
        this.loadUnreadCount()
      }, 60000)
    },
    stopUnreadCountPolling() {
      if (this.unreadCountTimer) {
        window.clearInterval(this.unreadCountTimer)
        this.unreadCountTimer = null
      }
    },
    async loadUnreadCount() {
      const response = await fetchUserNotificationUnreadCount()
      this.unreadCount = response.data?.unreadCount ?? response.data ?? 0
    }
  }
}
</script>

<style scoped>
.workspace-shell {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 280px 1fr;
  background: #edf2f7;
}

.side-nav {
  padding: 28px 20px;
  background: linear-gradient(180deg, #0f172a 0%, #1e293b 100%);
  color: #f8fafc;
}

.brand-block {
  padding: 12px 10px 28px;
}

.brand-block p,
.brand-block span {
  margin: 0;
  color: #94a3b8;
}

.brand-block h2 {
  margin: 8px 0;
  font-size: 30px;
}

.nav-menu {
  background: transparent;
  border-right: none;
}

.nav-menu :deep(.el-menu-item) {
  position: relative;
  margin-bottom: 8px;
  min-height: 52px;
  border-radius: 16px;
  color: #cbd5e1;
  font-weight: 600;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.nav-menu :deep(.el-menu-item .el-icon) {
  margin-right: 10px;
  font-size: 18px;
  color: #94a3b8;
  transition: color 0.2s ease;
}

.nav-menu :deep(.el-menu-item:hover) {
  background: rgba(255, 255, 255, 0.08);
  color: #f8fafc;
  transform: translateX(4px);
}

.nav-menu :deep(.el-menu-item:hover .el-icon) {
  color: #bfdbfe;
}

.nav-menu :deep(.el-menu-item.is-active) {
  background: linear-gradient(90deg, rgba(59, 130, 246, 0.22), rgba(37, 99, 235, 0.12));
  color: #eff6ff;
  box-shadow: inset 0 0 0 1px rgba(96, 165, 250, 0.18);
}

.nav-menu :deep(.el-menu-item.is-active .el-icon) {
  color: #dbeafe;
}

.nav-menu :deep(.el-menu-item.is-active::before) {
  content: '';
  position: absolute;
  left: 10px;
  top: 12px;
  bottom: 12px;
  width: 4px;
  border-radius: 999px;
  background: #93c5fd;
}

.workspace-main {
  padding: 28px;
}

.top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
}

.page-kicker {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.top-bar h1 {
  margin: 10px 0 0;
  font-size: 34px;
  color: #0f172a;
}

.top-actions {
  flex-shrink: 0;
}

@media (max-width: 1200px) {
  .workspace-shell {
    grid-template-columns: 1fr;
  }

  .side-nav {
    padding-bottom: 12px;
  }
}

@media (max-width: 768px) {
  .workspace-main {
    padding: 18px 12px 28px;
  }

  .top-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .top-actions {
    width: 100%;
  }

  .top-bar h1 {
    font-size: 28px;
  }
}
</style>
